import { useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { DynamicForm } from 'src/components/Forms';
import { getDynamicFormDefaultValues } from 'src/components/Forms/DynamicForm/dynamic-form-default-values';
import { ModalContainer } from 'src/components/ModalContainer';
import {
  ProjectStages,
  SubmitProposalLostPayload,
  useProjectStages,
  useSubmitProposalLost,
} from 'src/graphql';
import { Modals, useModalMethods, useModalState } from 'src/store/modals';
import { ProposalLostFormValues, useProposalLostForm } from './use-proposal-lost-form';

export const ProposalLostModal = () => {
  const { closeModal } = useModalMethods(Modals.ProposalLost);
  const { isOpened, modalProps } = useModalState(Modals.ProposalLost);

  const { proposalLostFormFields } = useProposalLostForm();
  const { submitProposalLost } = useSubmitProposalLost();
  const { projectStages } = useProjectStages({
    filters: [
      {
        attribute: 'projectStageId',
        operator: '=',
        value: ProjectStages.ProposalLost.toString(),
      },
    ],
    limit: 1,
  });

  const formMethods = useForm<ProposalLostFormValues>({
    defaultValues: getDynamicFormDefaultValues<ProposalLostFormValues>(proposalLostFormFields),
    mode: 'onChange',
  });
  const { formState, handleSubmit } = formMethods;

  const onSubmit = useCallback(
    async (data: ProposalLostFormValues) => {
      const project = modalProps?.project;
      const address = modalProps?.address;

      if ((!project?.id && !project?.projectId) || (!address?.id && !address?.addressId)) {
        return;
      }

      const payload: SubmitProposalLostPayload = {
        projectId: project.projectId || project.id,
        addressId: address.addressId || address.id,
        partnerId: address.partnerId,
        projectLostReasonId: data.projectLostReason.id,
        lostReasonNotes: data.lostReasonNotes,
      };

      closeModal();
      await submitProposalLost(
        payload,
        {
          ...project,
          projectStage: projectStages?.[0],
        },
        address,
      );
    },
    [closeModal, modalProps?.address, modalProps?.project, projectStages, submitProposalLost],
  );

  return (
    <ModalContainer
      open={isOpened}
      size="medium"
      title="Proposal Lost"
      actions={[
        { label: 'Cancel', kind: 'secondary', testId: 'proposal-lost-cancel' },
        {
          label: 'Mark as lost',
          kind: 'primary',
          onClick: handleSubmit(onSubmit),
          disabled: !formState.isValid,
          testId: 'proposal-lost-submit',
        },
      ]}
      onClose={closeModal}
      data-testid="proposal-lost-modal"
    >
      <DynamicForm fields={proposalLostFormFields} formMethods={formMethods} sx={{ gap: 2 }} />
    </ModalContainer>
  );
};
